import {
	BackofficeFilterForm,
	FilterForm,
	FilterFormFooter,
	FilterFormHeader
} from "components/BackofficeFilterForm";
import Input from "components/Input";
import MultiSelect from "components/SelectForm/MultiSelect";
import { useFormik } from "formik";
import { DateTimeHelper, ErrorHelper } from "helpers";
import OptionHelper from "helpers/OptionHelper";
import { useCallback, useEffect } from "react";
import { ApplicationService } from "services/applicationService";
import { ApplicationFilterOptions } from "services/types";

export interface ApplicationRegistryFilters extends Record<string, unknown> {
	from: string;
	to: string;
	lastAnalysisStatuses?: string[];
	interventionTypes: string[];
	concessionaires: string[];
	responsibleGroups: string[];
}
const allGroups = true;

function getFromMaxDate(to: string | undefined) {
	if (to) return to;
	return DateTimeHelper.currentDate();
}

function getToMinDate(from: string | undefined) {
	return from;
}

function getToMaxDate() {
	return DateTimeHelper.currentDate();
}

export default function BackofficeApplicationRegistryFilterForm({
	initialFilters,
	setFilters,
	clearFilters,
	isOpen,
	setIsOpen,
	filterOptions,
	setFilterOptions,
	isLoadingFilterOptions,
	setIsLoadingFilterOptions
}: Readonly<{
	initialFilters: ApplicationRegistryFilters;
	setFilters: (newFilters: ApplicationRegistryFilters) => void;
	clearFilters: () => void;
	isOpen: boolean;
	setIsOpen: (newIsOpen: boolean) => void;
	filterOptions: ApplicationFilterOptions | null;
	setFilterOptions: (newOptions: ApplicationFilterOptions) => void;
	isLoadingFilterOptions: boolean;
	setIsLoadingFilterOptions: (newIsLoadingFilterOptions: boolean) => void;
}>): JSX.Element {
	const formik = useFormik({
		initialValues: {
			lastAnalysisStatuses: [],
			...initialFilters
		},
		enableReinitialize: true,
		onSubmit: (newFilters) => {
			setFilters(newFilters);
			setIsOpen(false);
		}
	});
	const applicationService = ApplicationService.getInstance();

	const loadOptions = useCallback(() => {
		setIsLoadingFilterOptions(true);
		applicationService
			.getApplicationFilterOptions(allGroups)
			.then((responseData) => {
				setFilterOptions(responseData);
			})
			.catch((err) => ErrorHelper.showErrorMessage(err))
			.finally(() => setIsLoadingFilterOptions(false));
	}, [applicationService, setFilterOptions, setIsLoadingFilterOptions]);
	useEffect(() => {
		if (filterOptions == null && !isLoadingFilterOptions) loadOptions();
	}, []);

	const {
		values,
		setFieldValue,
		handleChange,
		handleSubmit,
		submitForm,
		dirty,
		errors
	} = formik;
	const hasErrors = !!Object.keys(errors).length;

	return (
		<BackofficeFilterForm
			isOpen={isOpen}
			onClickOutside={() => setIsOpen(false)}
		>
			<FilterFormHeader title="Filtros" onClose={() => setIsOpen(false)} />
			<FilterForm
				onSubmit={handleSubmit}
				isLoading={!filterOptions || isLoadingFilterOptions}
			>
				{filterOptions && (
					<>
						<div>Buscar por data de criação:</div>
						<fieldset className="flex flex-col sm:flex-row gap-4">
							<Input
								type="date"
								name="from"
								value={values.from}
								label="De: "
								onChange={handleChange}
								required={false}
								showOptionalIndicator={false}
								max={getFromMaxDate(values.to)}
							/>
							<Input
								type="date"
								name="to"
								value={values.to}
								label="Até: "
								onChange={handleChange}
								required={false}
								showOptionalIndicator={false}
								min={getToMinDate(values.from)}
								max={getToMaxDate()}
							/>
						</fieldset>

						<MultiSelect
							label="Concessionárias"
							name="concessionaires"
							value={values.concessionaires}
							options={filterOptions.concessionaires}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>

						<MultiSelect
							label="Status da última análise"
							name="lastAnalysisStatuses"
							value={values.lastAnalysisStatuses}
							options={filterOptions.analysisStatuses}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>

						<MultiSelect
							label="Grupos responsáveis"
							name="responsibleGroups"
							value={values.responsibleGroups}
							options={filterOptions.groups}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>

						<MultiSelect
							label="Tipos de intervenção"
							name="interventionTypes"
							value={values.interventionTypes}
							options={filterOptions.interventionTypes.map(
								OptionHelper.stringToOption
							)}
							isDisabled={false}
							setFieldValue={setFieldValue}
						/>
					</>
				)}
			</FilterForm>
			<FilterFormFooter
				dirty={dirty}
				hasErrors={hasErrors}
				onClear={() => {
					formik.resetForm();
					clearFilters();
				}}
				onSubmit={submitForm}
			/>
		</BackofficeFilterForm>
	);
}
